/**
 * The Suggested Resource picker (RP-18.2) — an inline popover, anchored to the Who cell.
 *
 * Deliberately NOT a modal: the Who column is a 48px chip inside a grid, and taking over
 * the screen to note a name is out of all proportion to the action. The popover opens next
 * to the chip that summoned it, so the row you are annotating stays visible.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SHOWS, AND WHO SEES IT
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-33 (2026-10-17): the list is the bench for THIS Plan Line — filtered to its Labor
 * Category's Discipline and rating range (Show all lifts that and labels mismatches) and
 * ranked by Fit Score, then NOS hours free over the line's months (decision 2026-10-19,
 * reversing 2026-10-17's points ranking). Rating, availability, other plans and the Fit
 * Score arrive only for an actor holding `viewResourceDetail: any` — since #189 that is
 * every role that reaches this picker, [ACCT] included. RP-18.2's original "no capacity"
 * carve-out was overtaken by that decision. This component renders what it is given; it
 * does not know the actor's role and must not learn.
 *
 * The Fit Score shows on R1–R4 lines only (RP-33 Decision 1): Supporting never feeds
 * Constraint Match or Expectation, so a score there would answer a question nobody asks.
 *
 * Confirming records who the plan PROPOSES. On its own it puts nobody on the Pod: no
 * assignment, no capacity effect. The commit is the plan-level Staffed status (product
 * decision 2026-10-06, src/api/plan-staffing.ts).
 *
 * The same picker adds a Candidate (RP-32, `mode="candidate"`): one list, one ranking.
 *
 * Debounced search with `onMouseDown` preventDefault on results, plus keyboard support
 * (↑↓ to move, Enter to pick, Escape to close), because a 40-line grid is driven from the
 * keyboard.
 */

import { Check, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AvailabilitySummary,
  OtherPlansPill,
} from "@/components/plans/ResourceAvailabilitySummary";
import { FitBadge } from "@/components/shared/FitBadge";
import { ReservationBadge } from "@/components/shared/ReservationBadge";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { usePlanResourcePicker } from "@/hooks/use-resource-plans";
import { windowLabel } from "@/lib/resource-availability";
import { SUGGESTED_RESOURCE_REASON_LABELS } from "@/lib/suggested-resource-reason";
import { cn } from "@/lib/utils";
import {
  type PlanLineView,
  type ResourcePickerEntryView,
  type ResourcePickerFitView,
  SUGGESTED_RESOURCE_REASONS,
  type SuggestedResourceReason,
} from "@/schemas/resource-plans";

const WIDTH = 340;
const MARGIN = 8;

interface Props {
  planId: string;
  line: PlanLineView;
  /** The Who chip that opened this. The popover positions itself beside it. */
  anchor: HTMLElement;
  onClose: () => void;
  onPick: (resourceId: string | null, confirmed: boolean) => void;
  /** RP-32.1 — set or clear why the current Suggested Resource was named. */
  onSetReason?: (reason: SuggestedResourceReason | null, note: string | null) => void;
  pending: boolean;
  /**
   * `"candidate"` (RP-32.2): the same list adds a Candidate. No current-suggestion block, no
   * reason, and `excludeIds` (the Suggested Resource and existing Candidates) are disabled.
   */
  mode?: "suggest" | "candidate";
  excludeIds?: ReadonlySet<string>;
}

/**
 * Why this candidate scored what they scored — the no-black-box rule (Story E3) applied to
 * a chip that would otherwise be a bare number next to a Resource's name.
 */
export function fitExplanation(fit: ResourcePickerFitView): string {
  const parts: string[] = [];
  if (fit.constraintMatch !== null) {
    parts.push(
      `Constraint Match ${fit.constraintMatch}% — confirmed on ${fit.ratedConstraints} of this Solution's ${fit.totalConstraints} Constraint${fit.totalConstraints === 1 ? "" : "s"}`,
    );
  }
  if (fit.expectation !== null) {
    parts.push(
      `Expectation ${fit.expectation}% — ${fit.disciplineCode ?? "Discipline"} rating ${fit.disciplineRating ?? "none confirmed, scored as 1"} against Expectation Level ${fit.expectationLevel}`,
    );
  }
  const partial =
    fit.partial === true
      ? ` The ~ means only one dimension had inputs${fit.expectation === null ? " — this Plan Line's Labor Category maps to no Discipline" : " — this Solution carries no Constraints"}, so this score is NOT comparable with a two-dimension one on another line.`
      : "";
  // Labelled "Fit for this Solution" so it is never read as the Pod's Fit Score: one
  // Resource against the Solution, Availability dropped (RP-33 Decision 2).
  return `Fit for this Solution — ${parts.join(". ")}.${partial} Availability is a Pod-wide figure and is not part of a single candidate's score.`;
}

/**
 * RP-32.1 — why this Resource. "Requested Resource" is a tooltip word only: the field is the
 * Suggested Resource, and "requested" would imply an obligation the plan does not carry.
 */
function ReasonControl({
  line,
  pending,
  onSetReason,
}: {
  line: PlanLineView;
  pending: boolean;
  onSetReason: NonNullable<Props["onSetReason"]>;
}) {
  const current = line.suggestedResourceReason ?? null;
  const [choice, setChoice] = useState<SuggestedResourceReason | "">(current ?? "");
  const [note, setNote] = useState(line.suggestedResourceReasonNote ?? "");
  const noteChanged = note.trim() !== (line.suggestedResourceReasonNote ?? "").trim();
  const needsNote = choice === "Other" && note.trim() === "";

  return (
    <div className="flex flex-col gap-1 border-b border-border px-3 py-1.5">
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <span
          className="cursor-help"
          title="Why this Resource was suggested — sometimes called the Requested Resource. Client Requested tells the RM that changing them is a client conversation."
        >
          Reason
        </span>
        <select
          value={choice}
          disabled={pending}
          aria-label="Reason for the Suggested Resource"
          onChange={(event) => {
            const value = event.target.value;
            const next = SUGGESTED_RESOURCE_REASONS.find((r) => r === value) ?? null;
            setChoice(next ?? "");
            // Other waits for its note; every other choice saves at once.
            if (next !== "Other") onSetReason(next, next === null ? null : note.trim() || null);
          }}
          className="h-7 flex-1 rounded-md border border-input bg-background px-2 text-xs text-foreground"
        >
          <option value="">No reason given</option>
          {SUGGESTED_RESOURCE_REASONS.map((r) => (
            <option key={r} value={r}>
              {SUGGESTED_RESOURCE_REASON_LABELS[r]}
            </option>
          ))}
        </select>
      </label>
      {choice !== "" && (
        <div className="flex items-center gap-1.5">
          <input
            value={note}
            maxLength={200}
            disabled={pending}
            onChange={(event) => setNote(event.target.value)}
            placeholder={choice === "Other" ? "Say why (required)" : "Add a note (optional)"}
            aria-label="Reason note"
            className="h-7 min-w-0 flex-1 rounded-md border border-border bg-card px-2 text-xs outline-none focus:border-primary"
          />
          {(noteChanged || (choice === "Other" && current !== "Other")) && (
            <button
              type="button"
              disabled={pending || needsNote}
              onClick={() => onSetReason(choice, note.trim() || null)}
              className="rounded border border-border px-1.5 py-0.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-50"
            >
              Save
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function SuggestedResourcePicker({
  planId,
  line,
  anchor,
  onClose,
  onPick,
  onSetReason,
  pending,
  mode = "suggest",
  excludeIds,
}: Props) {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebouncedValue(search, 250);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePlanResourcePicker(planId, debounced, true, line.id, showAll);
  const context = data?.pages[0]?.context ?? null;

  /**
   * Position against the anchor, flipping above it when there is no room below and
   * clamping so it never leaves the viewport.
   *
   * A ResizeObserver rather than a `results` dependency: the panel's height changes when
   * the result list does, and observing the panel is the direct cause rather than a proxy
   * for it — a trigger-only dependency would also be a dependency the effect never reads.
   */
  const reposition = useCallback(() => {
    const box = anchor.getBoundingClientRect();
    const height = panelRef.current?.offsetHeight ?? 320;
    const below = box.bottom + MARGIN;
    const top =
      below + height > window.innerHeight ? Math.max(MARGIN, box.top - height - MARGIN) : below;
    const left = Math.min(
      Math.max(MARGIN, box.left - WIDTH / 2 + box.width / 2),
      window.innerWidth - WIDTH - MARGIN,
    );
    setPos({ top, left });
  }, [anchor]);

  useLayoutEffect(() => {
    reposition();
    const panel = panelRef.current;
    if (panel === null) return;
    const observer = new ResizeObserver(reposition);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [reposition]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Dismiss on an outside click or Escape, the way a popover should.
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (event.target instanceof Node && panelRef.current?.contains(event.target) !== true) {
        onClose();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    // Deferred so the click that opened the popover does not immediately close it.
    const id = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const list: ResourcePickerEntryView[] = data?.pages.flatMap((p) => p.data) ?? [];
  const total = data?.pages[0]?.total ?? 0;
  const isExcluded = (id: string) => excludeIds?.has(id) === true;
  // Clamped at render rather than reset in an effect — a new result set cannot leave the
  // highlight pointing past the end, and there is no frame where it does.
  const active = Math.min(activeIndex, Math.max(0, list.length - 1));

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={mode === "candidate" ? "Add Candidate" : "Suggested Resource"}
      className="fixed z-50 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
      style={{ width: WIDTH, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
    >
      <div className="flex items-start justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {mode === "candidate" ? "Add Candidate" : "Suggested Resource"}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {line.laborCategoryName}
            {line.clientLaborCategory !== null && ` · ${line.clientLaborCategory}`}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {mode === "suggest" && line.suggestedResourceId !== null && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-muted/30 px-3 py-1.5">
          <span className="text-sm font-medium">{line.suggestedResourceName}</span>
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-xs font-semibold",
              line.suggestedResourceConfirmed
                ? "bg-primary/15 text-primary"
                : "bg-muted text-muted-foreground",
            )}
          >
            {line.suggestedResourceConfirmed ? "Confirmed" : "Proposed"}
          </span>
          <span className="ml-auto flex items-center gap-1">
            {!line.suggestedResourceConfirmed && (
              <button
                type="button"
                disabled={pending}
                onClick={() => onPick(line.suggestedResourceId, true)}
                className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-xs font-medium transition-colors hover:bg-muted"
              >
                <Check className="h-2.5 w-2.5" />
                Confirm
              </button>
            )}
            <button
              type="button"
              disabled={pending}
              onClick={() => onPick(null, false)}
              className="rounded border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Clear
            </button>
          </span>
        </div>
      )}
      {mode === "suggest" && onSetReason !== undefined && line.suggestedResourceId !== null && (
        <ReasonControl
          // Remount when the Resource changes: a reason belongs to them, never their replacement.
          key={line.suggestedResourceId}
          line={line}
          pending={pending}
          onSetReason={onSetReason}
        />
      )}

      <div className="px-3 py-2">
        <input
          ref={inputRef}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={(event) => {
            // The keyboard support ResourceSearchBox lacks.
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex(Math.min(list.length - 1, active + 1));
              return;
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex(Math.max(0, active - 1));
              return;
            }
            if (event.key === "Enter") {
              event.preventDefault();
              const picked = list[active];
              if (picked && !isExcluded(picked.id)) onPick(picked.id, false);
            }
          }}
          placeholder="Search Resources by name…"
          aria-label="Search Resources"
          className="h-7 w-full rounded-md border border-border bg-card px-2 text-sm outline-none focus:border-primary"
        />

        {context !== null && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {context.disciplineCode !== null ? (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 font-medium",
                  context.filtered ? "bg-primary/10 text-primary" : "bg-muted",
                )}
                title="This line's Labor Category Discipline and expected rating range. Unrated Resources are left out unless you show all."
              >
                {context.disciplineCode}
                {context.ratingRange !== null &&
                  ` · Rating ${context.ratingRange.min}–${context.ratingRange.max}`}
              </span>
            ) : (
              <span title="This line's Labor Category maps to no Discipline, so every Discipline is listed.">
                Any Discipline
              </span>
            )}
            <span>
              Ranked by {context.primary ? "fit, then " : ""}hours free
              {context.basis === "Monthly" ? " a month" : ""}, {windowLabel(context.window)}
            </span>
            {context.disciplineCode !== null && (
              <label className="ml-auto inline-flex cursor-pointer items-center gap-1">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(event) => {
                    setShowAll(event.target.checked);
                    setActiveIndex(0);
                  }}
                />
                Show all
              </label>
            )}
          </div>
        )}

        <div className="mt-1.5 max-h-56 overflow-y-auto rounded-md border border-border">
          {isLoading && <p className="px-2 py-3 text-xs text-muted-foreground">Searching…</p>}
          {isError && (
            <div className="px-2 py-3">
              <p className="text-xs text-destructive">
                {error instanceof Error ? error.message : "Could not load Resources."}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-1 text-xs font-medium underline"
              >
                Retry
              </button>
            </div>
          )}
          {!isLoading && !isError && list.length === 0 && (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              {context?.filtered === true
                ? `No one holding ${context.disciplineCode ?? "this Discipline"}${context.ratingRange === null ? "" : ` rated ${context.ratingRange.min}–${context.ratingRange.max}`} matches${debounced.trim() === "" ? "" : ` "${debounced.trim()}"`}. Tick Show all to see everyone.`
                : debounced.trim() === ""
                  ? "No active Resources."
                  : `No active Resource matches "${debounced.trim()}".`}
            </p>
          )}
          {list.map((entry, index) => {
            const excluded = isExcluded(entry.id);
            return (
              <button
                key={entry.id}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => onPick(entry.id, false)}
                disabled={pending || excluded}
                title={excluded ? "Already on this line" : undefined}
                className={cn(
                  "flex w-full flex-col items-start gap-0.5 border-b border-border px-2 py-1.5 text-left transition-colors last:border-b-0 disabled:opacity-60",
                  index === active ? "bg-muted" : "hover:bg-muted/50",
                )}
              >
                <span className="flex flex-wrap items-center gap-1">
                  <span className="text-sm font-medium">{entry.name}</span>
                  {entry.disciplines.map((d) => (
                    <span
                      key={d.code}
                      className="rounded bg-background px-1 text-xs font-semibold text-muted-foreground"
                      title={d.name}
                    >
                      {d.code}
                    </span>
                  ))}
                  {entry.disciplines.length === 0 && (
                    <span
                      className="cursor-help text-xs text-muted-foreground/70"
                      title="This Resource holds no Discipline. Selectable, but flagged: the Labor Category's mapped Discipline is what a Pod Position expects."
                    >
                      no Discipline
                    </span>
                  )}
                  {entry.rating !== null && (
                    <span className="font-mono text-xs text-muted-foreground">
                      Rating {entry.rating}
                    </span>
                  )}
                  {entry.mismatch?.discipline === true && (
                    <span className="rounded bg-warning-100 px-1 text-xs font-medium text-warning-950">
                      Discipline mismatch
                    </span>
                  )}
                  {entry.mismatch !== null &&
                    entry.mismatch.rating !== null &&
                    context?.ratingRange != null && (
                      <span className="rounded bg-warning-100 px-1 text-xs font-medium text-warning-950">
                        {entry.mismatch.rating === "unrated"
                          ? "Unrated"
                          : `Rating ${entry.rating ?? "?"} vs ${context.ratingRange.min}–${context.ratingRange.max}`}
                      </span>
                    )}
                  {excluded && <span className="text-xs text-muted-foreground">on this line</span>}
                </span>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {entry.availability !== null && (
                    <AvailabilitySummary availability={entry.availability} />
                  )}
                  <OtherPlansPill plans={entry.otherPlans} count={entry.otherPlanCount} />
                  {entry.reservation !== null && (
                    <ReservationBadge
                      accountName={entry.reservation.accountName}
                      until={entry.reservation.until}
                      forThisAccount={entry.reservation.forThisAccount}
                    />
                  )}
                  {context?.primary === true && (
                    <FitBadge
                      fit={entry.fit}
                      size="xs"
                      title={entry.fit === null ? undefined : fitExplanation(entry.fit)}
                      fallback={<span />}
                    />
                  )}
                  {entry.priorAccounts.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      Worked with {entry.priorAccounts.map((a) => a.name).join(", ")}
                    </span>
                  )}
                </span>
                {entry.reason !== null && (
                  <span className="text-xs text-muted-foreground">{entry.reason}</span>
                )}
              </button>
            );
          })}
          {hasNextPage && (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="w-full px-2 py-1.5 text-xs font-medium text-primary hover:bg-muted/50"
            >
              {isFetchingNextPage ? "Loading…" : `Load more (${list.length} of ${total})`}
            </button>
          )}
        </div>

        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          {context?.primary === true
            ? "Fit is this Solution's score asked of one Resource. "
            : "No Fit Score on a Supporting line — Supporting never feeds fit quality. "}
          {context?.guidanceUnavailable === true
            ? "NOS couldn't be reached, so hours free is missing. "
            : "Hours free is NOS capacity minus NOS guidance, never cost. "}
          ↑↓ to move, Enter to pick.
        </p>
      </div>

      {/*
       * Confirming one line records who the plan PROPOSES and books nothing. Putting
       * Resources on the Pod is the plan-level Staffed commit (product decision 2026-10-06).
       */}
      <p className="rounded-b-lg border-t border-border bg-muted/30 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
        {mode === "candidate" ? (
          <>
            A <span className="font-semibold text-foreground">Candidate</span> is the RM&rsquo;s
            shortlist — up to three per line. It books no capacity and is never shown to the client.
          </>
        ) : (
          <>
            <span className="font-semibold text-foreground">Confirm</span> records who this plan
            proposes. On its own it books no capacity — confirmed Resources go onto the
            Solution&rsquo;s Pod when the plan&rsquo;s Resourcing status is set to{" "}
            <span className="font-medium">Staffed</span>.
          </>
        )}
      </p>
    </div>
  );
}
